import { garminDeviceLabel } from '../garmin/GarminAttribution';
import { forwardRef, useMemo } from 'react';
import {
  ComposedChart, Area, XAxis, YAxis, CartesianGrid,
  ReferenceLine, ResponsiveContainer, Tooltip,
} from 'recharts';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmtDuration = (s) => {
  if (s == null || s <= 0) return '-';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
};

const fmtElapsed = (s) => {
  if (s == null) return '-';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

const fmtPace = (minKm) => {
  if (!minKm) return '-';
  const m = Math.floor(minKm);
  const s = Math.round((minKm - m) * 60);
  return `${m}:${String(s).padStart(2, '0')}/km`;
};

const getUtcOffsetMinutes = (iso) => {
  if (!iso) return null;
  const m = iso.match(/([+-])(\d{2}):(\d{2})$/);
  if (!m) return null;
  return (m[1] === '+' ? 1 : -1) * (parseInt(m[2]) * 60 + parseInt(m[3]));
};

const fmtTiming = (elapsedS, workoutStartTime) => {
  if (elapsedS == null || !workoutStartTime) return '-';
  const startMs = new Date(workoutStartTime).getTime();
  const arrivalMs = startMs + elapsedS * 1000;
  const offsetMin = getUtcOffsetMinutes(workoutStartTime);
  const offsetMs = (offsetMin !== null ? offsetMin : 0) * 60000;
  const arrLocalMs = arrivalMs + offsetMs;
  const startLocalMs = startMs + offsetMs;
  const startLocalDate = new Date(startLocalMs);
  const midnightMs = startLocalMs - (
    startLocalDate.getUTCHours() * 3600000 +
    startLocalDate.getUTCMinutes() * 60000 +
    startLocalDate.getUTCSeconds() * 1000
  );
  const dayNum = Math.floor((arrLocalMs - midnightMs) / 86400000) + 1;
  const d = new Date(arrLocalMs);
  return `Day ${dayNum} ${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}`;
};

const SEGMENT_TYPE_LABEL = {
  climb: 'Climb', descend: 'Descent', rolling: 'Rolling',
  mixed: 'Mixed', checkpoint: 'Stop',
};

// ---------------------------------------------------------------------------
// Elevation data builder (from raw chunks)
// ---------------------------------------------------------------------------
const buildElevationData = (chunks) => {
  const pts = [];
  let cumDist = 0;
  for (const c of chunks) {
    const d = c.distance || 0;
    cumDist += d;
    const alt = c.avg_altitude ?? null;
    if (alt != null) pts.push({ distKm: cumDist / 1000, altitude: Math.round(alt) });
  }
  return pts;
};

// ---------------------------------------------------------------------------
// Plan segment boundaries (same logic as RunningWorkoutChartsSection)
// ---------------------------------------------------------------------------
const getPlanBoundaries = (racePlanSegments, matchedCheckpoints) => {
  const cpDistSet = new Set(
    (matchedCheckpoints || [])
      .filter(cp => cp.fit_distance_km != null && cp.status !== 'undetected')
      .map(cp => cp.fit_distance_km.toFixed(3))
  );
  const maxEndM = Math.max(...racePlanSegments.map(s => s.end_distance_m));
  const seen = new Set();
  const boundaries = [];
  for (const seg of racePlanSegments) {
    if (seg.segment_type === 'checkpoint') continue;
    const distKm = seg.end_distance_m / 1000;
    const key = distKm.toFixed(3);
    if (seen.has(key)) continue;
    seen.add(key);
    if (seg.end_distance_m >= maxEndM) continue;
    if (cpDistSet.has(key)) continue;
    boundaries.push({ distKm, label: seg.end_label });
  }
  return boundaries;
};

// ---------------------------------------------------------------------------
// PlanPrintContent
// ---------------------------------------------------------------------------
const PlanPrintContent = forwardRef(({
  chunks = [],
  matchedCheckpoints = [],
  racePlanSegments = [],
  race = null,
  workoutStartTime = null,
  fileSource = null,
  deviceName = null,
}, ref) => {
  const elevData = useMemo(() => buildElevationData(chunks), [chunks]);
  const planBoundaries = useMemo(
    () => getPlanBoundaries(racePlanSegments, matchedCheckpoints),
    [racePlanSegments, matchedCheckpoints]
  );

  const hasTiming = !!workoutStartTime && racePlanSegments.some(s => s.elapsed_s != null);
  const hasCutoffs = racePlanSegments.some(s => s.cutoff);

  const root = {
    background: '#fff', color: '#111',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: 12,
    padding: '16px 20px',
  };

  const th = {
    background: '#374151', color: '#fff',
    padding: '5px 8px', textAlign: 'left',
    fontWeight: 'bold', fontSize: 11,
    border: '1px solid #d1d5db',
  };

  const td = (isStop) => ({
    padding: '4px 8px',
    border: '1px solid #d1d5db',
    background: isStop ? '#dbeafe' : undefined,
    color: isStop ? '#1e40af' : undefined,
    fontWeight: isStop ? 'bold' : undefined,
    whiteSpace: 'nowrap',
  });

  return (
    <div ref={ref} style={root}>
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#111' }}>
          {race?.name ? `${race.name} — Checkpoints Plan` : 'Checkpoints Plan'}
        </h2>
        {/* The Garmin brand guidelines extend to printouts and exported
            visuals, so the device credit prints directly under the title. */}
        {fileSource === 'garmin' && (
          <div style={{ fontSize: 11, color: '#444', marginTop: 2 }}>
            {garminDeviceLabel(deviceName)}
          </div>
        )}
      </div>

      {/* Elevation chart */}
      {elevData.length > 0 && (
        <div style={{ width: '100%', height: 220, marginBottom: 20 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={elevData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="distKm"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={v => `${v.toFixed(0)} km`}
                tick={{ fontSize: 10, fill: '#374151' }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#374151' }}
                tickFormatter={v => `${v}m`}
                width={45}
              />
              <Tooltip
                formatter={(v) => [`${v}m`, 'Altitude']}
                labelFormatter={(v) => `${Number(v).toFixed(1)} km`}
              />
              <Area
                type="monotone" dataKey="altitude"
                stroke="#4b6fa5" fill="#bfdbfe"
                strokeWidth={1.5} dot={false}
              />

              {/* Checkpoint lines — green */}
              {matchedCheckpoints
                .filter(cp => cp.status !== 'undetected' && cp.fit_distance_km != null)
                .map((cp, idx) => (
                  <ReferenceLine
                    key={`cp-${idx}`}
                    x={cp.fit_distance_km}
                    stroke="#16a34a"
                    strokeWidth={1.5}
                    strokeDasharray="5 4"
                    label={{ value: cp.name, position: 'top', fill: '#16a34a', fontSize: 9, angle: -45, offset: 4 }}
                  />
                ))
              }

              {/* Plan boundary lines — orange */}
              {planBoundaries.map(({ distKm, label }, idx) => (
                <ReferenceLine
                  key={`pb-${idx}`}
                  x={distKm}
                  stroke="#ea580c"
                  strokeWidth={1}
                  strokeDasharray="4 3"
                  label={{ value: label, position: 'top', fill: '#ea580c', fontSize: 9, angle: -45, offset: 4 }}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Plan table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            <th style={th}>Segment</th>
            <th style={th}>Type</th>
            <th style={{ ...th, textAlign: 'right' }}>End km</th>
            <th style={{ ...th, textAlign: 'right' }}>Length</th>
            <th style={{ ...th, textAlign: 'right' }}>Gain</th>
            <th style={{ ...th, textAlign: 'right' }}>Loss</th>
            <th style={{ ...th, textAlign: 'right' }}>∑ D+</th>
            <th style={{ ...th, textAlign: 'right' }}>Elapsed</th>
            {hasTiming && <th style={th}>Timing</th>}
            {hasCutoffs && <th style={th}>Cutoff</th>}
            <th style={{ ...th, textAlign: 'right' }}>Duration</th>
            <th style={{ ...th, textAlign: 'right' }}>Pace</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            let cumGain = 0;
            return racePlanSegments.map((seg, idx) => {
              if (seg.segment_type !== 'checkpoint') cumGain += (seg.elevation_gain_m || 0);
              const isStop = seg.segment_type === 'checkpoint';
              const cellStyle = td(isStop);
              return (
                <tr key={idx} style={{ background: idx % 2 === 0 && !isStop ? '#f9fafb' : undefined }}>
                  <td style={cellStyle}>
                    {isStop ? `⏸ ${seg.start_label}` : `${seg.start_label} → ${seg.end_label}`}
                  </td>
                  <td style={cellStyle}>{SEGMENT_TYPE_LABEL[seg.segment_type] || seg.segment_type}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {seg.end_distance_m != null ? `${(seg.end_distance_m / 1000).toFixed(1)}` : ''}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {isStop ? '' : `${(seg.length_m / 1000).toFixed(1)} km`}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {isStop || !seg.elevation_gain_m ? '' : `+${Math.round(seg.elevation_gain_m)}m`}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {isStop || !seg.elevation_loss_m ? '' : `-${Math.round(seg.elevation_loss_m)}m`}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {cumGain > 0 ? `+${Math.round(cumGain)}m` : '-'}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {fmtElapsed(seg.elapsed_s)}
                  </td>
                  {hasTiming && (
                    <td style={cellStyle}>{fmtTiming(seg.elapsed_s, workoutStartTime)}</td>
                  )}
                  {hasCutoffs && (
                    <td style={cellStyle}>{seg.cutoff || '-'}</td>
                  )}
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {isStop ? fmtDuration(seg.stopping_time_s) : fmtDuration(seg.duration_s)}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {fmtPace(seg.moving_pace)}
                  </td>
                </tr>
              );
            });
          })()}
        </tbody>
      </table>
    </div>
  );
});

PlanPrintContent.displayName = 'PlanPrintContent';
export default PlanPrintContent;
